import React, { useEffect, useState } from "react";
import API_BASE from "../config/api";
import Swal from "sweetalert2";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";



export default function Profile({ userData }) {
    const [searchParams] = useSearchParams();
    const listinguid = searchParams.get("listinguid");
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({});
    const [originalData, setOriginalData] = useState({});

    const [provinces, setProvinces] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [municipalities, setMunicipalities] = useState([]);

    const localUser = JSON.parse(localStorage.getItem("user"));
    const personaluid = localUser?.personaluid;
    const navigate = useNavigate();
    useEffect(() => {
        if (!listinguid) return;

        fetch(`${API_BASE}/api/get_single_listing.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ listinguid }),
        })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setFormData(data.data);
                    setOriginalData(data.data);

                    // Auto load dependent dropdowns
                    if (data.data.province) fetchDistricts(data.data.province);
                    if (data.data.district) fetchMunicipalities(data.data.district);
                }
            })
            .catch(err => console.log("Error:", err));
    }, [listinguid]);



    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    /* -------------------------------------
       LOAD PROVINCES / DISTRICTS / MUNICIPALITIES
       ------------------------------------- */
    useEffect(() => {
        fetchProvinces();
    }, []);

    const fetchProvinces = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/provinces.php`);
            const data = await res.json();
            setProvinces(data.provinces || []);
        } catch (err) {
            console.error("Province API Error:", err);
        }
    };

    const fetchDistricts = async (provinceId) => {
        try {
            const res = await fetch(`${API_BASE}/api/districts.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ province_id: provinceId }),
            });

            const data = await res.json();
            setDistricts(data.districts || []);
        } catch (err) {
            console.error("District API Error:", err);
        }
    };

    const fetchMunicipalities = async (districtId) => {
        try {
            const res = await fetch(`${API_BASE}/api/muncipality.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ district: districtId }),
            });

            const data = await res.json();
            setMunicipalities(data.municipalities || []);
        } catch (err) {
            console.error("Municipality API Error:", err);
        }
    };

    const handleProvinceChange = (provinceId) => {
        setFormData({
            ...formData,
            province: provinceId,
            district: "",
            muncipality: "",
        });
        fetchDistricts(provinceId);
        setMunicipalities([]);
    };

    const handleDistrictChange = (districtId) => {
        setFormData({
            ...formData,
            district: districtId,
            muncipality: "",
        });
        fetchMunicipalities(districtId);
    };

    /* -----------------------------------
       VALIDATION FUNCTION
       ----------------------------------- */
    const validateForm = () => {
        if (!formData.title?.trim()) return "Full Name is required";
        if (!formData.email?.includes("@")) return "Invalid email address";
        if (!/^[0-9]{7,15}$/.test(formData.mobile || "")) return "Invalid mobile number";
        if (formData.website && !formData.website.startsWith("http"))
            return "Website must start with http or https";

        if (!formData.province) return "Province is required";
        if (!formData.district) return "District is required";
        if (!formData.muncipality) return "Municipality is required";

        if (!formData.addresses?.trim()) return "Full address is required";

        return null;
    };

    /* -----------------------------------
       SAVE PROFILE
       ----------------------------------- */
    const handleSave = async () => {
        try {
            // Convert IDs → Names before saving
            const provinceName =
                provinces.find((p) => p.id == formData.province)?.provinceName || originalData.province;

            const districtName =
                districts.find((d) => d.id == formData.district)?.districtName || originalData.district;

            const municipalityName = formData.muncipality || originalData.muncipality;

            const finalData = {
                ...formData,
                province: provinceName,
                district: districtName,
                muncipality: municipalityName,
            };

            const res = await fetch(`${API_BASE}/api/update_profile.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(finalData),
            });

            const result = await res.json();

            if (result.success) {
                Swal.fire({
                    icon: "success",
                    title: "Profile Updated Successfully!",
                    timer: 2000,
                    showConfirmButton: false,
                });

                setEditMode(false);
                setOriginalData(finalData);
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: result.message,
                });
            }
        } catch (error) {
            console.error("Save Error:", error);
            Swal.fire({
                icon: "error",
                title: "Failed!",
                text: "Something went wrong.",
            });
        }
    };


    /* -----------------------------------
       UI COMPONENT
       ----------------------------------- */
    return (
        <div className="container py-4">
            <h2 className="fw-bold mb-3">My Profile</h2>

            <div className="card shadow-lg border-0 rounded-3">
                <div className="card-body p-4">

                    {/* IMAGE, NAME, EMAIL */}
                    <div className="text-center mb-4">

                        <img
                            src={
                                formData.profilepics
                                    ? `${API_BASE}/backend/uploads/${formData.profilepics}`
                                    : `${API_BASE}/assets/default.png`
                            }
                            alt="profile"
                            className="rounded-circle border shadow"
                            width="110"
                            height="110"
                        />

                        <h4 className="fw-semibold mt-3">{formData.title}</h4>
                        <div className="text-muted">{formData.email}</div>
                        <div className="text-muted">{formData.mobile}</div>
                    </div>

                    {/* PERSONAL INFORMATION */}
                    <h5 className="fw-bold mb-3">Personal Information</h5>
                    <div className="row g-3 mb-4">

                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Full Name</label>
                            <input
                                className="form-control"
                                name="title"
                                value={formData.title || ""}
                                disabled={!editMode}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Email Address</label>
                            <input
                                className="form-control"
                                name="email"
                                value={formData.email || ""}
                                disabled={!editMode}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Mobile Number</label>
                            <input
                                className="form-control"
                                name="mobile"
                                value={formData.mobile || ""}
                                disabled={!editMode}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Website</label>
                            <input
                                className="form-control"
                                name="website"
                                value={formData.website || ""}
                                disabled={!editMode}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* ADDRESS DETAILS */}
                    <h5 className="fw-bold mb-3">Address Details</h5>
                    <div className="row g-3 mb-4">

                        <div className="col-md-3">
                            <label className="form-label fw-semibold">Province</label>

                            {!editMode ? (
                                <input className="form-control" value={formData.province || ""} disabled />
                            ) : (
                                <select
                                    className="form-control"
                                    value={formData.province || ""}
                                    onChange={(e) => handleProvinceChange(e.target.value)}
                                >
                                    <option value="">Select Province</option>
                                    {provinces.map((p) => (
                                        <option key={p.id} value={p.id}>{p.provinceName}</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div className="col-md-3">
                            <label className="form-label fw-semibold">District</label>

                            {!editMode ? (
                                <input className="form-control" value={formData.district || ""} disabled />
                            ) : (
                                <select
                                    className="form-control"
                                    value={formData.district || ""}
                                    onChange={(e) => handleDistrictChange(e.target.value)}
                                >
                                    <option value="">Select District</option>
                                    {districts.map((d) => (
                                        <option key={d.id} value={d.id}>{d.districtName}</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div className="col-md-3">
                            <label className="form-label fw-semibold">Municipality</label>

                            {!editMode ? (
                                <input className="form-control" value={formData.muncipality || ""} disabled />
                            ) : (
                                <select
                                    className="form-control"
                                    value={formData.muncipality || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, muncipality: e.target.value })
                                    }
                                >
                                    <option value="">Select Municipality</option>
                                    {municipalities.map((m) => (
                                        <option key={m.id} value={m.municipalityName}>
                                            {m.municipalityName}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div className="col-md-3">
                            <label className="form-label fw-semibold">Ward</label>
                            <input
                                className="form-control"
                                name="ward"
                                value={formData.ward || ""}
                                disabled={!editMode}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="col-12">
                            <label className="form-label fw-semibold">Full Address</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                name="addresses"
                                value={formData.addresses || ""}
                                disabled={!editMode}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* SOCIAL LINKS */}
                    <h5 className="fw-bold mb-3">Social Profiles</h5>

                    <div className="row g-3 mb-4">
                        <div className="col-md-6">
                            <input
                                className="form-control"
                                placeholder="Facebook"
                                name="linkfacebook"
                                value={formData.linkfacebook || ""}
                                disabled={!editMode}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="col-md-6">
                            <input
                                className="form-control"
                                placeholder="Instagram"
                                name="linkinstagram"
                                value={formData.linkinstagram || ""}
                                disabled={!editMode}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="col-md-6">
                            <input
                                className="form-control"
                                placeholder="Twitter"
                                name="linktwitter"
                                value={formData.linktwitter || ""}
                                disabled={!editMode}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="col-md-6">
                            <input
                                className="form-control"
                                placeholder="LinkedIn"
                                name="linklinkedin"
                                value={formData.linklinkedin || ""}
                                disabled={!editMode}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* DESCRIPTION */}
                    <h5 className="fw-bold mb-2">About / Description</h5>
                    <textarea
                        className="form-control"
                        rows={4}
                        name="descriptions"
                        value={formData.descriptions || ""}
                        disabled={!editMode}
                        onChange={handleChange}
                    />
                    <div className="d-flex justify-content-between mt-4">

                        <button
                            className="btn btn-warning px-4"
                            onClick={() => navigate(`/admin?userid=${personaluid}`)}
                        >
                            ← Back
                        </button>

                        <div>
                            {!editMode ? (
                                <button className="btn btn-primary px-4" onClick={() => setEditMode(true)}>
                                    Edit Profile
                                </button>
                            ) : (
                                <>
                                    <button
                                        className="btn btn-secondary px-4 me-2"
                                        onClick={() => {
                                            setFormData(originalData);
                                            setDistricts([]);
                                            setMunicipalities([]);
                                            setEditMode(false);
                                        }}
                                    >
                                        Cancel
                                    </button>

                                    <button className="btn btn-success px-4" onClick={handleSave}>
                                        Save Changes
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
